import React, { useState, useMemo, useRef } from "react";
import { SxProps, Box, Grid } from "@mui/material";
import { StriimTypography } from "@striim/striim-ui-v2";
import { styles } from "../review/review.styles";
import { TablesList } from "../review/review";
import { tableSummaryStyles } from "./tables-summary.styles";
import { List, RowComponentProps } from "react-window";

interface TableSummaryProps {
    schemas?: string[];
    tables?: Record<string, string[]>;
    containerStyles?: SxProps;
    isObject: boolean;
    channel: string;
}

const ObjectTable = React.memo(({ objects, channel }: { objects: { schema: string }[]; channel: string }) => (
    <Grid sx={styles.tableWrapper}>
        <Grid sx={styles.tableTitle}>
            <StriimTypography variant="body4" sx={{ color: "greyscale.900" }}>
                List of Objects to load {channel ? `from ${channel}` : ""}: {objects.length}
            </StriimTypography>
        </Grid>
        <Grid sx={styles.tablesList}>
            <StriimTypography
                variant="body4"
                sx={{ color: "greyscale.700", whiteSpace: "pre-line", lineHeight: "26px" }}
            >
                {objects.map(item => item.schema).join("\n")}
            </StriimTypography>
        </Grid>
    </Grid>
));

export default function TableSummary({
    schemas = [],
    tables = {},
    containerStyles,
    isObject = false,
    channel
}: TableSummaryProps) {
    const [activeSchemaIndex, setActiveSchemaIndex] = useState(0);

    const currentTableComponent = useMemo(() => {
        if (isObject || !schemas[activeSchemaIndex]) return null;
        const schemaName = schemas[activeSchemaIndex];
        return <TablesList table={tables[schemaName]} name={schemaName} />;
    }, [activeSchemaIndex, schemas, tables, isObject]);

    const SchemaItems = ({
        index,
        style,
        schemas
    }: RowComponentProps<{
        schemas: string[];
    }>) => {
        const schemaName = schemas[index];
        const isSchemaActive = index === activeSchemaIndex;

        return (
            <Box
                style={style}
                onClick={() => {
                    setActiveSchemaIndex(index);
                }}
                sx={tableSummaryStyles.schemaItemContainer(isSchemaActive)}
                data-testid="schema-label"
            >
                <StriimTypography variant="body4" color={isSchemaActive ? "primary.700" : "greyscale.700"}>
                    {schemaName}
                </StriimTypography>
            </Box>
        );
    };

    const SchemasListMemoized = useMemo(
        () => (
            <Box sx={tableSummaryStyles.schemaContainer}>
                <List
                    rowComponent={SchemaItems}
                    rowHeight={45}
                    rowCount={schemas.length}
                    rowProps={{ schemas }}
                    height={600}
                    width="100%"
                />
            </Box>
        ),
        [schemas, activeSchemaIndex]
    );

    return (
        <Box sx={{ ...containerStyles, ...tableSummaryStyles.container }}>
            {isObject ? (
                <ObjectTable objects={schemas} channel={channel} />
            ) : (
                <>
                    {SchemasListMemoized}
                    <Box sx={tableSummaryStyles.tableContainer}>{currentTableComponent}</Box>
                </>
            )}
        </Box>
    );
}
